import { useState } from "react";
import { useAsync } from "react-async";
import { getUnreadMessages } from "../../../../api/services/messageService";
import { DashboardProps, DashboardPropsDefaults } from "../../types/dashboardProps.d";
import MessageDisplay from "../../../../api/models/messageDisplay";
import { getPendingInvoiceApprovals, getTsFlagsByDirectorUserId } from "../../../../api/services/timesheetService";
import Spinner from "../../../spinner";
import { getPendingTherapists } from "../../../../api/services/therapistService";
import User from "../../../../api/models/user";
import { getPendingCaseloads } from "../../../../api/services/caseloadService";
import caseloadDisplay from '../../../../api/models/caseloadDisplay';
import { useHistory } from "react-router-dom";
import DataLink from "../../../DataLink";
import { useDispatch } from "react-redux";
import { updateUnreadMessagesCount } from "../../../../slices/userSlice";
import DOMPurify from 'dompurify';

const DashboardWidgetBillingSpecialistNotifications = ({ user }: DashboardProps): JSX.Element => {
    const [messages, setMessages] = useState<MessageDisplay[]>([]);
    const [tsFlagsCount, setTsFlagsCount] = useState<number>(0);
    const [pendingTherapists, setPendingTherapists] = useState<User[]>([]);
    const [pendingCaseloads, setPendingCaseloads] = useState<caseloadDisplay[]>([]);
    const [proofingCount, setProofingCount] = useState<number>(0);
    const dispatch = useDispatch();

    const history = useHistory();
    const id: number = user.userId!;

    const asyncMessages = useAsync({
        promiseFn: getUnreadMessages, id,
        onResolve: (r) => {
            const sanitizedMessages = r.data.messages.map(message => ({
                ...message,
                messageText: DOMPurify.sanitize(message.messageText)
            }));

            setMessages(sanitizedMessages)
            dispatch(updateUnreadMessagesCount(r.data?.messages?.length ?? 0))
        }
    });

    const asyncStudentRequests = useAsync({
        promiseFn: getPendingTherapists,
        onResolve: (response: any) => setPendingTherapists(response.data)
    });

    const asyncPendingTherapists = useAsync({
        promiseFn: getPendingCaseloads,
        onResolve: (response) => setPendingCaseloads(response.data.caseloads)
    });

    const asyncTsFlags = useAsync({
        promiseFn: getTsFlagsByDirectorUserId,
        onResolve: (response: any) => setTsFlagsCount(response.data)
    });

    const asyncTs = useAsync({
        promiseFn: getPendingInvoiceApprovals,
        onResolve: (response) => setProofingCount(response.data.length)
    });

    return (
        <>
            <div className={DashboardPropsDefaults.containerClassNames}>
                <div className="pb-1 dashboard-widget-title">
                    <h3>Notifications</h3>
                </div>
                <div className={DashboardPropsDefaults.contentClassNames}>
                    <Spinner show={(asyncMessages.isPending || asyncTsFlags.isPending || asyncStudentRequests.isPending || asyncPendingTherapists.isPending || asyncTs.isPending)} />
                    {!(asyncMessages.isPending || asyncTsFlags.isPending || asyncStudentRequests.isPending || asyncPendingTherapists.isPending || asyncTs.isPending) && (<>
                        <div>You Have <DataLink href="/messages" className="mb-2">{messages.length}</DataLink> unread messages</div>
                        <div>Student Requests&nbsp;
                            <span className="mb-2 text-sm cursor-pointer">
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
                                (<DataLink href="/caseload/approve" options={{ referrer: 'dashboard' }}>{pendingCaseloads.length}</DataLink>)
                            </span>
                        </div>
                        <div>Therapist Profiles With Pending Updates&nbsp;
                            <span className="mb-2 text-sm underline cursor-pointer hover:no-underline">
                                (<DataLink href="/admin/therapists/approve">{pendingTherapists.length}</DataLink>)
                            </span>
                        </div>
                        <div>Timesheet Flags To Be Proofed&nbsp;
                            <span className="mb-2 text-sm underline cursor-pointer hover:no-underline">
                                (<DataLink href="/proofing">{tsFlagsCount}</DataLink>)
                            </span>
                        </div>
                        <div>Invoice Approvals Needed&nbsp;
                            <span className="mb-2 text-sm underline cursor-pointer hover:no-underline">
                                (<DataLink href="/accounting/invoice">{proofingCount}</DataLink>)
                            </span>
                        </div>
                    </>)}
                </div>
            </div>
        </>
    );
};

export default DashboardWidgetBillingSpecialistNotifications;
